'use client';

import { useState, useRef } from 'react';
import { VideoJobResponse } from '@/lib/types';
import api from '@/lib/api';
import { LazyImage } from '@/components/LazyImage';
import SearchPromptsButton from './SearchPromptsButton';

interface VideoCardProps {
  video: VideoJobResponse;
  onSelect?: (video: VideoJobResponse) => void;
  onCancel?: (jobId: string, force?: boolean) => void;
  onDelete?: (jobId: string) => void;
}

export default function VideoCard({ video, onSelect, onCancel, onDelete }: VideoCardProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const isCompleted = video.status === 'completed';
  const isPending = video.status === 'pending';
  const isProcessing = video.status === 'processing';
  const isFailed = video.status === 'failed';
  const isCancelled = video.status === 'cancelled';

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (isCompleted && video.video_filename && videoRef.current) {
      videoRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Calculate duration
  const duration = (video.frames / 24).toFixed(1);

  return (
    <div
      className="group relative aspect-video bg-[var(--bg-elevated)] rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-[var(--accent)]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => isCompleted && onSelect?.(video)}
    >
      {/* Thumbnail from source image */}
      {video.source_image && (
        <LazyImage
          src={api.getThumbnailUrl(video.source_image)}
          fallbackSrc={api.getImageUrl(video.source_image)}
          alt={video.prompt}
          className={`absolute inset-0 w-full h-full transition-opacity ${isPlaying ? 'opacity-0' : 'opacity-100'}`}
        />
      )}

      {/* Video preview on hover */}
      {isCompleted && video.video_filename && (
        <video
          ref={videoRef}
          src={api.getVideoUrl(video.video_filename)}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity ${isPlaying ? 'opacity-100' : 'opacity-0'}`}
          muted
          loop
          playsInline
        />
      )}

      {/* Trace this prompt through the image library (private mode only) */}
      <SearchPromptsButton
        text={video.prompt}
        className="absolute top-2 right-12 z-20 p-2 rounded-lg bg-black/70 text-white/70 opacity-0 group-hover:opacity-100 hover:bg-[var(--accent)] hover:text-black transition-all cursor-pointer"
      />

      {/* Delete button for completed/failed/cancelled videos */}
      {(isCompleted || isFailed || isCancelled) && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(video.job_id); }}
          className="absolute top-2 right-2 z-20 p-2 rounded-lg bg-black/70 text-white/70 opacity-0 group-hover:opacity-100 hover:bg-[var(--error)] hover:text-white transition-all cursor-pointer"
          title="Delete"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      )}

      {/* Processing/Pending overlay */}
      {(isPending || isProcessing) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60">
          {isProcessing ? (
            <>
              <div className="w-8 h-8 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin mb-2" />
              <span className="text-xs text-white/80">Generating...</span>
              {onCancel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(video.job_id, true);
                  }}
                  className="mt-3 px-3 py-1 text-xs bg-red-500/20 hover:bg-red-500/40 text-red-300 rounded transition-colors"
                  title="Force cancel stuck job"
                >
                  Force Cancel
                </button>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-white/80 mb-1">#{video.position || '?'}</div>
              <span className="text-xs text-white/60">In Queue</span>
              {onCancel && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancel(video.job_id);
                  }}
                  className="mt-3 px-3 py-1 text-xs bg-white/10 hover:bg-white/20 rounded transition-colors"
                >
                  Cancel
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Failed overlay */}
      {isFailed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-900/60">
          <svg className="w-8 h-8 text-red-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <span className="text-xs text-white/80">Failed</span>
          <span className="text-xs text-white/60 mt-1 px-2 text-center truncate max-w-full">{video.error}</span>
        </div>
      )}

      {/* Video icon badge */}
      {isCompleted && !isPlaying && (
        <div className="absolute top-2 left-2 p-1.5 bg-black/60 rounded">
          <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 010 1.972l-11.54 6.347a1.125 1.125 0 01-1.667-.986V5.653z" />
          </svg>
        </div>
      )}

      {/* Duration badge */}
      {isCompleted && (
        <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/60 rounded text-xs text-white">
          {duration}s
        </div>
      )}

      {/* Info overlay on hover */}
      {isHovering && isCompleted && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
          <p className="text-white text-sm truncate">{video.prompt}</p>
          <p className="text-white/60 text-xs mt-1">{video.width}x{video.height}</p>
        </div>
      )}
    </div>
  );
}
